import {useEffect, useRef} from 'react';
import {useThree} from '@react-three/fiber';
import {OrthographicCamera} from '@react-three/drei';

export const DETAIL_MIN_ZOOM_MULTIPLIER = 1;
export const DETAIL_MAX_ZOOM_MULTIPLIER = 5;
const ZOOM_STEP = 1.15;

const clampPan = (x, y, zoom, size, totalWidth, totalHeight) => {
    const visibleWidth = size.width / zoom;
    const visibleHeight = size.height / zoom;
    const maxX = visibleWidth >= totalWidth ? 0 : totalWidth / 2 - visibleWidth / 2;
    const maxY = visibleHeight >= totalHeight ? 0 : totalHeight / 2 - visibleHeight / 2;
    return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
    };
};

/**
 * Wheel zoom via camera; LMB pan updates React state → Layer group offset.
 * Camera position stays fixed: OrthographicCamera would overwrite pan if we moved it.
 */
const DetailMapCursorControls = ({
                                     enabled,
                                     zoom,
                                     onZoomChange,
                                     pan,
                                     onPanChange,
                                     panWidth,
                                     panHeight,
                                 }) => {
    const gl = useThree((s) => s.gl);
    const size = useThree((s) => s.size);
    const camera = useThree((s) => s.camera);
    const panRef = useRef(pan);
    panRef.current = pan;
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;

    useEffect(() => {
        camera.zoom = zoom;
        camera.updateProjectionMatrix();
        const clamped = clampPan(panRef.current.x, panRef.current.y, zoom, size, panWidth, panHeight);
        if (clamped.x !== panRef.current.x || clamped.y !== panRef.current.y) {
            onPanChange(clamped);
        }
    }, [camera, onPanChange, panHeight, panWidth, size, zoom]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        let dragging = false;
        let panArmed = false;
        let lastClientX = 0;
        let lastClientY = 0;
        const PAN_THRESHOLD_PX = 4;

        const onMouseDown = (event) => {
            if (event.button !== 0) {
                return;
            }
            dragging = true;
            panArmed = false;
            lastClientX = event.clientX;
            lastClientY = event.clientY;
        };

        const onMouseMove = (event) => {
            if (!dragging) {
                return;
            }
            const currentZoom = zoomRef.current;
            const dx = event.clientX - lastClientX;
            const dy = event.clientY - lastClientY;
            if (!panArmed) {
                if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) {
                    return;
                }
                panArmed = true;
            }
            lastClientX = event.clientX;
            lastClientY = event.clientY;
            // Drag content with the cursor (same feel as map grab).
            onPanChange((prev) => clampPan(
                prev.x + dx / currentZoom,
                prev.y - dy / currentZoom,
                currentZoom,
                size,
                panWidth,
                panHeight
            ));
        };

        const onMouseUp = () => {
            dragging = false;
            panArmed = false;
        };

        const onWheel = (event) => {
            event.preventDefault();
            onZoomChange((currentZoom) => {
                const step = event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
                return Math.min(
                    DETAIL_MAX_ZOOM_MULTIPLIER,
                    Math.max(DETAIL_MIN_ZOOM_MULTIPLIER, currentZoom * step)
                );
            });
        };

        const element = gl.domElement;
        element.style.touchAction = 'none';
        element.addEventListener('mousedown', onMouseDown, true);
        window.addEventListener('mousemove', onMouseMove, true);
        window.addEventListener('mouseup', onMouseUp, true);
        element.addEventListener('wheel', onWheel, {passive: false});
        return () => {
            element.removeEventListener('mousedown', onMouseDown, true);
            window.removeEventListener('mousemove', onMouseMove, true);
            window.removeEventListener('mouseup', onMouseUp, true);
            element.removeEventListener('wheel', onWheel);
        };
    }, [enabled, gl.domElement, onPanChange, onZoomChange, panHeight, panWidth, size]);

    return null;
};

const DetailMapControls = ({
                               enabled = true,
                               zoomMultiplier,
                               onZoomChange,
                               fitWidth,
                               fitHeight,
                               panWidth,
                               panHeight,
                               pan,
                               onPanChange,
                           }) => {
    const size = useThree((s) => s.size);
    const safeFitW = Math.max(fitWidth, 0.001);
    const safeFitH = Math.max(fitHeight, 0.001);
    const safePanW = Math.max(panWidth, safeFitW);
    const safePanH = Math.max(panHeight, safeFitH);
    const sizeW = Math.max(size.width, 1);
    const sizeH = Math.max(size.height, 1);
    const baseZoom = Math.min(sizeW / safeFitW, sizeH / safeFitH) * 0.9;
    const zoom = Math.max(baseZoom * zoomMultiplier, 0.001);

    return (
        <>
            <OrthographicCamera
                makeDefault
                position={[0, 0, 100]}
                zoom={zoom}
                near={0.1}
                far={1000}
            />
            <DetailMapCursorControls
                enabled={enabled}
                zoom={zoom}
                onZoomChange={onZoomChange}
                pan={pan}
                onPanChange={onPanChange}
                panWidth={safePanW}
                panHeight={safePanH}
            />
        </>
    );
};

export default DetailMapControls;
